import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { ChatPresenceStatus, initialsFor } from '../../models/chat.models';

/**
 * Avatar with an optional presence dot.
 *
 * Used everywhere a person appears — sidebar rows, message bubbles, the group member list — so
 * a missing photo degrades to initials consistently instead of each list inventing its own
 * fallback.
 */
@Component({
  selector: 'app-chat-avatar',
  standalone: true,
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <span class="chat-avatar" [style.width.px]="size" [style.height.px]="size" [title]="name">
      <img *ngIf="avatarUrl; else initialsTpl" [src]="avatarUrl" [alt]="name" (error)="onImageError()" />
      <ng-template #initialsTpl>
        <span class="chat-avatar__initials" [style.font-size.px]="size * 0.38">{{ initials }}</span>
      </ng-template>
      <span *ngIf="status" class="chat-avatar__dot" [ngClass]="'is-' + status" [style.width.px]="dotSize" [style.height.px]="dotSize"></span>
    </span>
  `,
  styles: [
    `
      .chat-avatar {
        position: relative;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        border-radius: 50%;
        background: var(--secondary);
        color: var(--primary);
        flex: 0 0 auto;
        overflow: visible;

        img {
          width: 100%;
          height: 100%;
          border-radius: 50%;
          object-fit: cover;
        }
      }

      .chat-avatar__initials {
        font-weight: 600;
        letter-spacing: 0.02em;
        user-select: none;
      }

      .chat-avatar__dot {
        position: absolute;
        right: -1px;
        bottom: -1px;
        border-radius: 50%;
        border: 2px solid var(--white);
        box-sizing: content-box;

        &.is-online {
          background: var(--green);
        }

        &.is-dnd {
          background: var(--dangerLight);
        }

        &.is-offline {
          background: var(--lightDarkGray);
        }
      }
    `,
  ],
})
export class ChatAvatarComponent {
  @Input() name = '';
  @Input() size = 36;

  /** Omit to render without a presence dot (e.g. inside a message bubble). */
  @Input() status: ChatPresenceStatus | null = null;

  @Input() set avatar(value: string | null | undefined) {
    this.avatarUrl = value && !ChatAvatarComponent.deadUrls.has(value) ? value : null;
  }

  avatarUrl: string | null = null;

  get initials(): string {
    return initialsFor(this.name);
  }

  get dotSize(): number {
    return Math.max(8, Math.round(this.size * 0.28));
  }

  /**
   * Shared across every avatar on the page: the same broken photo is often bound by many
   * instances at once (a sender's avatar next to a run of messages, sidebar row, group member
   * list), and without this each one fires and fails its own identical request instead of one
   * instance learning the URL is dead for everybody.
   */
  private static readonly deadUrls = new Set<string>();

  /** A dead photo URL falls back to initials rather than a broken-image icon. */
  onImageError(): void {
    if (this.avatarUrl) {
      ChatAvatarComponent.deadUrls.add(this.avatarUrl);
    }
    this.avatarUrl = null;
  }
}
